import { useQueries, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { explorarFuente, obtenerFuentes } from '../api/consultas'
import Portada from './Portada'

const IDIOMAS = ['es', 'es-419', 'en', 'all']

/**
 * Busca la misma serie en el resto de fuentes.
 *
 * Existe sobre todo por MangaDex: retira las series con licencia oficial
 * (Solo Leveling, One-Punch Man, Frieren...) y deja la ficha sin un solo
 * capitulo. Sin esto habria que salir, ir a Buscar y reescribir el titulo a
 * mano cada vez.
 */
export default function OtrasFuentes({
  titulo,
  fuenteActual,
}: {
  titulo: string
  fuenteActual?: string
}) {
  const [buscando, setBuscando] = useState(false)

  const { data: fuentes } = useQuery({ queryKey: ['fuentes'], queryFn: obtenerFuentes })

  const candidatas = useMemo(() => {
    const utiles = (fuentes ?? []).filter(
      (f) => IDIOMAS.includes(f.lang) && f.name !== 'Local source',
    )
    const actual = utiles.find((f) => f.id === fuenteActual)

    // MangaDex tiene una fuente por idioma y saldria repetida varias veces.
    // Se deja una sola por nombre, prefiriendo el espanol, y se descarta la
    // fuente en la que ya estas.
    const porNombre = new Map<string, (typeof utiles)[number]>()
    for (const fuente of utiles) {
      if (fuente.name === actual?.name) continue
      const previa = porNombre.get(fuente.name)
      if (!previa || (!previa.lang.startsWith('es') && fuente.lang.startsWith('es'))) {
        porNombre.set(fuente.name, fuente)
      }
    }
    return [...porNombre.values()]
  }, [fuentes, fuenteActual])

  const resultados = useQueries({
    queries: candidatas.map((fuente) => ({
      queryKey: ['buscar', fuente.id, titulo],
      queryFn: () => explorarFuente(fuente.id, 'SEARCH' as const, 1, titulo),
      enabled: buscando,
      retry: false,
      staleTime: 10 * 60 * 1000,
    })),
  })

  if (!buscando) {
    return (
      <button className="boton secundario" onClick={() => setBuscando(true)}>
        Buscar en otras fuentes
      </button>
    )
  }

  const terminadas = resultados.filter((r) => !r.isFetching).length
  const enCurso = terminadas < candidatas.length

  // Solo el primer resultado de cada fuente: es casi siempre el que coincide,
  // y una lista larga de casi-aciertos estorba mas que ayuda.
  const encontrados = candidatas
    .map((fuente, i) => ({ fuente, manga: resultados[i]?.data?.mangas[0] }))
    .filter((x) => x.manga)

  return (
    <div className="otras-fuentes">
      {enCurso && (
        <p className="cargando">
          Buscando… {terminadas} de {candidatas.length} fuentes
        </p>
      )}

      {encontrados.length > 0 && (
        <ul className="lista">
          {encontrados.map(({ fuente, manga }) => (
            <li key={fuente.id}>
              <Link to={`/manga/${manga!.id}`}>
                <div style={{ width: 40, flexShrink: 0 }}>
                  <Portada ruta={manga!.thumbnailUrl} titulo={manga!.title} />
                </div>
                <span style={{ flex: 1 }}>
                  {manga!.title}
                  <span className="meta">{fuente.name}</span>
                </span>
                <span className="etiqueta-idioma">{fuente.lang}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {!enCurso && encontrados.length === 0 && (
        <div className="aviso">
          <strong>No aparece en ninguna otra fuente</strong>
          <p style={{ fontSize: 13 }}>
            Prueba desde la pestana Buscar con el titulo original en ingles o en japones
            romanizado: muchas fuentes no indexan el titulo traducido.
          </p>
        </div>
      )}
    </div>
  )
}
